import { AnnotationSetType } from 'types/types'
import { reactive, readonly } from 'vue'

import { queryBarMitzvahAnnotations } from '@/store/queryHelpers'

// Module-level singleton — shared across all AnnotatedText instances and useTextColoration.
// Reactive Map: when cache.set() is called, any computed reading cache.get() reactively updates.
// Only the internal fetchAnnotations function mutates the cache; consumers get a read-only view.
const _cache = reactive(new Map<string, AnnotationSetType[]>())
const pendingFetches = new Map<string, Promise<AnnotationSetType[]>>()

export function useAnnotationCache() {
  function fetchAnnotations(username: string): Promise<AnnotationSetType[]> {
    // Deduplicate concurrent fetches for the same username
    const pending = pendingFetches.get(username)
    if (pending) {
      return pending
    }

    const promise = queryBarMitzvahAnnotations(username)
      .then((sets) => {
        const totalAnnotations = sets.reduce((sum, s) => sum + (s.annotations?.length ?? 0), 0)
        console.log('useAnnotationCache.fetchAnnotations:', username, '→', sets.length, 'sets,', totalAnnotations, 'total annotations')
        _cache.set(username, sets)
        return sets
      })
      .catch((error) => {
        console.error('useAnnotationCache.fetchAnnotations failed:', error)
        return _cache.get(username) ?? []
      })
      .finally(() => {
        pendingFetches.delete(username)
      })

    pendingFetches.set(username, promise)
    return promise
  }

  /** Refetch annotations for a username, updating all reactive consumers. */
  function invalidate(username: string): Promise<AnnotationSetType[]> {
    return fetchAnnotations(username)
  }

  // Expose cache as readonly reactive — consumers can read but not mutate directly.
  // readonly() provides runtime enforcement (unlike a bare type cast).
  return { cache: readonly(_cache) as ReadonlyMap<string, AnnotationSetType[]>, fetchAnnotations, invalidate }
}
